<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import ProgressRing from 'dashboard/components-next/upload/ProgressRing.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { t } = useI18n();

const {
  filteredCurrentChatAttachments,
  attachments,
  hasMoreAttachments,
  loadMoreAttachments,
} = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

// Optimistic "uploading" bubble: a local objectURL preview shows while the
// DirectUpload runs; a progress ring overlays it. On completion the server copy
// (same echo_id) replaces this message.
const isUploading = computed(
  () => attachment.value?.uploadStatus === 'uploading'
);
const uploadProgress = computed(() =>
  typeof attachment.value?.uploadProgress === 'number'
    ? attachment.value.uploadProgress
    : null
);

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const showGallery = ref(false);
const isDownloading = ref(false);

onMounted(() => {
  if (attachment.value?.dataUrl) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const handleImageError = () => {
  hasError.value = true;
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="image"
    @click="!isUploading && (showGallery = true)"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-lg">
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <!-- Em upload mas o preview local ainda não carregou: anel sobre placeholder
         (não fica vazio). Quando carrega, cai no branch isLoaded abaixo. -->
    <div
      v-else-if="isUploading && !isLoaded"
      class="grid place-items-center rounded-lg bg-n-slate-3 min-w-[12rem] min-h-[8rem]"
    >
      <ProgressRing :value="uploadProgress" :size="48" :stroke="4" />
    </div>
    <div v-else-if="isLoaded" class="relative group rounded-lg overflow-hidden">
      <img
        class="skip-context-menu"
        :src="attachment.dataUrl"
        :width="attachment.width"
        :height="attachment.height"
        loading="lazy"
      />
      <!-- Upload em andamento: anel de progresso sobre o preview local. -->
      <div
        v-if="isUploading"
        class="absolute inset-0 z-10 flex items-center justify-center bg-n-black/40 backdrop-blur-[1px]"
      >
        <ProgressRing :value="uploadProgress" :size="48" :stroke="4" />
      </div>
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div
        v-if="!isUploading"
        class="absolute right-2 bottom-2 hidden group-hover:flex gap-2"
      >
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    :has-more="hasMoreAttachments"
    @load-more="loadMoreAttachments"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
